/**
 * DiaryQuizStage - 메타인지 다이어리 Stage Component
 *
 * 특징:
 * - 2문제씩 페이지네이션
 * - 정오답 체크 없음 (답안 제출만)
 * - 마지막 페이지에서만 제출 버튼 표시
 * - 재진입 시 이전 응답 복원
 * - New UI 퀴즈 컴포넌트 사용 (NewFillBlankQuiz, NewMultiChoiceQuiz, NewSelectTextQuiz)
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import {
    ChevronLeft,
    ChevronRight,
    Loader2,
    ArrowLeft,
    CheckCircle,
    BookOpen,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { quizScriptApi } from '@/api/quizScript'
import {
    learningSessionApi,
    type DiaryQuizDataResponse,
    type CompleteSessionResponse,
    type DiaryQuizResponseItem,
} from '@/api/learningSession'
import type {
    QuizScript,
    FillBlankQuizData,
    MultiChoiceQuizData,
    SelectTextQuizData,
    QuizData,
} from '@quebon/shared-learning'
import {
    FillBlankQuizForDiary,
    MultiChoiceQuizForDiary,
    SelectTextQuizForDiary,
} from '@quebon/shared-learning'
import { toast } from 'sonner'

// ============================================================================
// Types
// ============================================================================

export interface DiaryQuizStageProps {
    lessonId: number
    lessonTitle: string
    componentProgressId: number
    diaryQuizData: DiaryQuizDataResponse
    /** Class product ID for B2B context (Configuration lookup) */
    classProductId?: number
    onComplete: (response: CompleteSessionResponse) => void
    onBack: () => void
}

interface QuizAnswer {
    quizId: string
    scriptId: string | null
    answers: string[]
    vars: Record<string, unknown>
}

const QUESTIONS_PER_PAGE = 2

// ============================================================================
// Component
// ============================================================================

export function DiaryQuizStage({
    lessonId,
    lessonTitle,
    componentProgressId,
    diaryQuizData,
    classProductId,
    onComplete,
    onBack,
}: DiaryQuizStageProps) {
    const { t } = useTranslation('learner')

    // Quiz scripts cache
    const [quizScripts, setQuizScripts] = useState<Map<string, QuizScript>>(new Map())
    const quizScriptsRef = useRef(quizScripts)
    quizScriptsRef.current = quizScripts
    const [isLoadingScripts, setIsLoadingScripts] = useState(false)

    // Current page (0-indexed)
    const [currentPage, setCurrentPage] = useState(0)

    // Answers map: quizId -> answer data
    const [answers, setAnswers] = useState<Map<string, QuizAnswer>>(() => {
        // Restore previous responses on re-entry
        const initialMap = new Map<string, QuizAnswer>()
        if (diaryQuizData.previousResponses && diaryQuizData.previousResponses.length > 0) {
            diaryQuizData.previousResponses.forEach(prev => {
                if (prev.submittedResponse && Array.isArray(prev.submittedResponse)) {
                    const quizItem = diaryQuizData.quizzes.find(q => q.quizId === prev.quizId)
                    initialMap.set(prev.quizId, {
                        quizId: prev.quizId,
                        scriptId: quizItem?.scriptId || null,
                        answers: prev.submittedResponse.map(a => String(a)),
                        vars: {},
                    })
                }
            })
        }
        return initialMap
    })
    const answersRef = useRef(answers)
    answersRef.current = answers

    // Submission state
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isCompleted, setIsCompleted] = useState(false)

    // Computed values
    const quizItems = diaryQuizData.quizzes
    const totalPages = Math.ceil(quizItems.length / QUESTIONS_PER_PAGE)
    const isLastPage = currentPage === totalPages - 1

    // Current page quiz items
    const currentPageQuizzes = useMemo(() => {
        const startIdx = currentPage * QUESTIONS_PER_PAGE
        return quizItems.slice(startIdx, startIdx + QUESTIONS_PER_PAGE)
    }, [quizItems, currentPage])

    // Load quiz scripts for current page (uses ref to avoid dependency issues)
    const loadQuizScriptsForPage = useCallback(async (pageQuizzes: typeof quizItems) => {
        const quizzesToLoad = pageQuizzes.filter(q => !quizScriptsRef.current.has(q.quizId))
        if (quizzesToLoad.length === 0) return

        setIsLoadingScripts(true)
        try {
            const loadPromises = quizzesToLoad.map(async q => {
                try {
                    const script = await quizScriptApi.getQuiz(q.quizId, false)
                    return { quizId: q.quizId, script }
                } catch (error) {
                    console.error(`Failed to load quiz script ${q.quizId}:`, error)
                    return null
                }
            })

            const results = await Promise.all(loadPromises)
            setQuizScripts(prev => {
                const newMap = new Map(prev)
                results.forEach(r => {
                    if (r) newMap.set(r.quizId, r.script)
                })
                return newMap
            })
        } finally {
            setIsLoadingScripts(false)
        }
    }, []) // Stable callback - uses ref for quizScripts check

    // Load scripts when page changes
    useEffect(() => {
        loadQuizScriptsForPage(currentPageQuizzes)
    }, [currentPageQuizzes]) // loadQuizScriptsForPage is stable via ref

    // Convert QuizScript to QuizData (stable helper – no hooks)
    const getQuizData = (quizScript: QuizScript): QuizData | null => {
        if (!quizScript) return null

        const rawData = quizScript as unknown as Record<string, unknown>
        const qd = (rawData.quizData as QuizData) || quizScript

        return {
            ...qd,
            type: quizScript.type || qd.type,
            title: quizScript.title || qd.title,
            problem: quizScript.problem || qd.problem,
            vars: quizScript.vars || qd.vars,
            image: quizScript.image || qd.image,
            imageMobile: quizScript.imageMobile || qd.imageMobile,
        } as QuizData
    }

    // Memoize quiz data per page to keep object references stable across renders.
    // Without this, getQuizData creates new objects every render, causing child
    // components' auto-submit useEffects to fire (quiz.vars ref changes) → infinite loop.
    const quizDataMap = useMemo(() => {
        const map = new Map<string, QuizData | null>()
        currentPageQuizzes.forEach(q => {
            const quizScript = quizScripts.get(q.quizId)
            map.set(q.quizId, quizScript ? getQuizData(quizScript) : null)
        })
        return map
    }, [currentPageQuizzes, quizScripts])

    // Handle quiz answer change (called by New* components)
    const handleQuizSolve = useCallback((
        quizId: string | number,
        newAnswers: string[],
        vars: Record<string, unknown>,
        _elapsedTime: number
    ) => {
        const id = String(quizId)
        const quizItem = quizItems.find(q => q.quizId === id)

        setAnswers(prev => {
            const newMap = new Map(prev)
            newMap.set(id, {
                quizId: id,
                scriptId: quizItem?.scriptId || null,
                answers: newAnswers,
                vars,
            })
            return newMap
        })
    }, [quizItems])

    // Check if all questions on current page are answered
    const isCurrentPageComplete = useMemo(() => {
        return currentPageQuizzes.every(q => {
            const answer = answers.get(q.quizId)
            return answer && answer.answers.length > 0 && answer.answers.some(a => a.trim() !== '')
        })
    }, [currentPageQuizzes, answers])

    // Navigation handlers
    const handlePrevious = useCallback(() => {
        if (currentPage > 0) {
            setCurrentPage(prev => prev - 1)
        }
    }, [currentPage])

    const handleNext = useCallback(() => {
        if (!isCurrentPageComplete) {
            toast.error(t('learning.diaryQuiz.answerAllQuestions'))
            return
        }
        if (currentPage < totalPages - 1) {
            setCurrentPage(prev => prev + 1)
        }
    }, [currentPage, totalPages, isCurrentPageComplete])

    // Check if all questions are answered
    const allQuestionsAnswered = useMemo(() => {
        return quizItems.every(q => {
            const answer = answers.get(q.quizId)
            return answer && answer.answers.length > 0 && answer.answers.some(a => a.trim() !== '')
        })
    }, [quizItems, answers])

    // Submit all answers
    const handleSubmit = useCallback(async () => {
        if (!allQuestionsAnswered) {
            toast.error(t('learning.diaryQuiz.answerAllQuestions'))
            return
        }
        if (isSubmitting) return

        setIsSubmitting(true)
        try {
            // Build responses array
            const responses: DiaryQuizResponseItem[] = quizItems.map(q => {
                const answer = answers.get(q.quizId)!
                return {
                    quizId: answer.quizId,
                    scriptId: answer.scriptId,
                    answers: answer.answers,
                    vars: answer.vars,
                }
            })

            const submitResponse = await learningSessionApi.submitDiaryQuizResponses(
                componentProgressId,
                { responses, classProductId }
            )

            setIsCompleted(true)

            // Convert to CompleteSessionResponse format
            const completeResponse: CompleteSessionResponse = {
                componentProgressId: submitResponse.componentProgressId,
                status: 'completed',
                completedAt: submitResponse.completedAt,
                totalQuestions: submitResponse.totalResponses,
            }
            onComplete(completeResponse)
        } catch (error) {
            console.error('Failed to submit diary quiz:', error)
            toast.error(t('learning.diaryQuiz.submitError'))
        } finally {
            setIsSubmitting(false)
        }
    }, [allQuestionsAnswered, isSubmitting, quizItems, answers, componentProgressId, classProductId, onComplete])

    // Get initial answers for a quiz
    const getInitialAnswers = useCallback((quizId: string): string[] | undefined => {
        const answer = answers.get(quizId)
        return answer?.answers
    }, [answers])

    // ========================================================================
    // Render: Loading state
    // ========================================================================
    if (isLoadingScripts && quizScripts.size === 0) {
        return (
            <div className="flex flex-col justify-center items-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
                <p className="mt-4 text-gray-600">{t('learning.diaryQuiz.loadingDiary')}</p>
            </div>
        )
    }

    // ========================================================================
    // Render: Completed state
    // ========================================================================
    if (isCompleted) {
        return (
            <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="p-8 text-center">
                    <div className="flex justify-center items-center mx-auto mb-6 w-24 h-24 bg-emerald-100 rounded-full">
                        <CheckCircle className="w-12 h-12 text-emerald-600" />
                    </div>
                    <h2 className="mb-2 text-2xl font-bold text-gray-900">
                        {t('learning.diaryQuiz.completedTitle')}
                    </h2>
                    <p className="mb-6 text-lg text-gray-600">
                        {t('learning.diaryQuiz.completedMessage')}
                    </p>
                    <button
                        onClick={onBack}
                        className="px-8 py-3 font-semibold text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-700"
                    >
                        {t('learning.diaryQuiz.goBack')}
                    </button>
                </div>
            </div>
        )
    }

    // ========================================================================
    // Render: No quizzes
    // ========================================================================
    if (quizItems.length === 0) {
        return (
            <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex flex-col justify-center items-center p-8 text-center">
                    <p className="mb-4 text-gray-500">{t('learning.diaryQuiz.noQuestions')}</p>
                    <button
                        onClick={onBack}
                        className="flex gap-2 items-center text-indigo-600 hover:text-indigo-800"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t('learning.diaryQuiz.goBack')}
                    </button>
                </div>
            </div>
        )
    }

    // ========================================================================
    // Render: Main UI
    // ========================================================================
    return (
        <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600">
                <div className="flex justify-between items-center">
                    <div>
                        <button
                            onClick={onBack}
                            className="flex gap-2 items-center mb-2 text-sm text-emerald-200 transition-colors hover:text-white"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            {t('learning.diaryQuiz.goBack')}
                        </button>
                        <h2 className="flex gap-2 items-center text-xl font-bold text-white">
                            <BookOpen className="w-5 h-5" />
                            {t('learning.diaryQuiz.powerOfQuestions')}
                        </h2>
                        <p className="text-sm text-emerald-200">
                            {t('learning.diaryQuiz.description')}
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-2xl font-bold text-white">
                            {currentPage + 1} / {totalPages}
                        </p>
                        <p className="text-sm text-emerald-200">
                            {t('learning.diaryQuiz.progress', { done: answers.size, total: quizItems.length })}
                        </p>
                    </div>
                </div>
            </div>

            {/* Progress bar */}
            <div className="h-1 bg-emerald-100">
                <div
                    className="h-full bg-emerald-500 transition-all duration-300"
                    style={{ width: `${((currentPage + 1) / totalPages) * 100}%` }}
                />
            </div>

            {/* Quiz Content - 2 questions per page */}
            <div className="p-6 space-y-8">
                {isLoadingScripts ? (
                    <div className="flex flex-col justify-center items-center py-12">
                        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
                        <p className="mt-3 text-gray-500">{t('learning.diaryQuiz.loadingQuestion')}</p>
                    </div>
                ) : (
                    currentPageQuizzes.map((quizItem, idx) => {
                        const quizData = quizDataMap.get(quizItem.quizId) ?? null
                        const questionNumber = currentPage * QUESTIONS_PER_PAGE + idx + 1

                        if (!quizData) {
                            return (
                                <div key={quizItem.quizId} className="p-4 bg-gray-50 rounded-xl">
                                    <p className="text-gray-500">{t('learning.diaryQuiz.cannotLoadQuestion')}</p>
                                </div>
                            )
                        }

                        return (
                            <div
                                key={quizItem.quizId}
                                className="p-6 bg-gray-50 rounded-xl border border-gray-100"
                            >
                                {/* Question number badge */}
                                <div className="mb-4">
                                    <span className="px-3 py-1 text-sm font-semibold text-emerald-700 bg-emerald-100 rounded-full">
                                        Q{questionNumber}
                                    </span>
                                </div>

                                {/* Quiz renderer based on type */}
                                {quizData.type === 'fill_blank' && (
                                    <FillBlankQuizForDiary
                                        key={quizItem.quizId}
                                        quizId={quizItem.quizId}
                                        quiz={quizData as FillBlankQuizData}
                                        onSolve={handleQuizSolve}
                                        initialAnswers={answersRef.current.get(quizItem.quizId)?.answers}
                                        hideSubmitButton={true}
                                    />
                                )}
                                {quizData.type === 'multi_choice' && (
                                    <MultiChoiceQuizForDiary
                                        key={quizItem.quizId}
                                        quizId={quizItem.quizId}
                                        quiz={quizData as MultiChoiceQuizData}
                                        onSolve={handleQuizSolve}
                                        initialAnswers={answersRef.current.get(quizItem.quizId)?.answers}
                                        hideSubmitButton={true}
                                    />
                                )}
                                {quizData.type === 'select_text' && (
                                    <SelectTextQuizForDiary
                                        key={quizItem.quizId}
                                        quizId={quizItem.quizId}
                                        quiz={quizData as SelectTextQuizData}
                                        onSolve={handleQuizSolve}
                                        initialAnswers={answersRef.current.get(quizItem.quizId)?.answers}
                                        hideSubmitButton={true}
                                    />
                                )}

                                {/* Unsupported type fallback */}
                                {!['fill_blank', 'multi_choice', 'select_text'].includes(quizData.type) && (
                                    <div className="p-4 text-amber-700 bg-amber-50 rounded-lg">
                                        <p>{t('learning.diaryQuiz.unsupportedType', { type: quizData.type })}</p>
                                    </div>
                                )}
                            </div>
                        )
                    })
                )}
            </div>

            {/* Footer Navigation */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex justify-between items-center mx-auto max-w-3xl">
                    {/* Previous button */}
                    <button
                        onClick={handlePrevious}
                        disabled={currentPage === 0}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
                            currentPage === 0
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-gray-700 hover:bg-gray-100'
                        )}
                    >
                        <ChevronLeft className="w-5 h-5" />
                        {t('learning.diaryQuiz.previous')}
                    </button>

                    {/* Page indicator */}
                    <div className="flex gap-1.5">
                        {Array.from({ length: totalPages }).map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => setCurrentPage(idx)}
                                className={cn(
                                    'w-2.5 h-2.5 rounded-full transition-all',
                                    idx === currentPage
                                        ? 'bg-emerald-500 w-6'
                                        : 'bg-gray-300 hover:bg-gray-400'
                                )}
                            />
                        ))}
                    </div>

                    {/* Next / Submit button */}
                    {!isLastPage ? (
                        <button
                            onClick={handleNext}
                            className="flex gap-2 items-center px-4 py-2 font-medium text-emerald-600 rounded-lg transition-colors hover:bg-emerald-50"
                        >
                            {t('learning.diaryQuiz.next')}
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className={cn(
                                'flex gap-2 items-center px-6 py-2 font-medium rounded-lg transition-colors',
                                !allQuestionsAnswered || isSubmitting
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                            )}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {t('learning.diaryQuiz.submitting')}
                                </>
                            ) : (
                                <>
                                    {t('learning.diaryQuiz.submit')}
                                    <CheckCircle className="w-5 h-5" />
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

export default DiaryQuizStage
